import React from 'react';

import {
  Alert,
  notification
} from 'antd';

import ConfigProvider from 'antd/lib/config-provider';
import deDE from 'antd/lib/locale/de_DE';
import enGB from 'antd/lib/locale/en_GB';

import {
  defaults as OlControlDefaults
} from 'ol/control';
import OlControlScaleLine from 'ol/control/ScaleLine';
import {
  defaults as OlInteractionDefaults
} from 'ol/interaction';
import OlInteractionDragRotate from 'ol/interaction/DragRotate';
import OlLayerTile from 'ol/layer/Tile';
import OlMap from 'ol/Map';
import {
  fromLonLat,
  transformExtent
} from 'ol/proj';
import OlSourceTileWMS from 'ol/source/TileWMS';
import OlView from 'ol/View';
import {
  render
} from 'react-dom';
import {
  Provider
} from 'react-redux';

import Logger from '@terrestris/base-util/dist/Logger';
import UrlUtil from '@terrestris/base-util/dist/UrlUtil/UrlUtil';

import MapContext from '@terrestris/react-geo/dist/Context/MapContext/MapContext';

import SHOGunApplicationUtil from '@terrestris/shogun-util/dist/parser/SHOGunApplicationUtil';

import SHOGunAPIClient from '@terrestris/shogun-util/dist/service/SHOGunAPIClient';

import App from './App';
import i18n from './i18n';
import {
  store
} from './store/store';

import './index.less';

const getConfigLang = (lang: string) => {
  switch (lang) {
    case 'en':
      return enGB;
    case 'de':
      return deDE;
    default:
      return enGB;
  }
};

const setupMap = async () => {
  const applicationId = UrlUtil.getQueryParam(window.location.href, 'applicationId');

  if (applicationId) {
    Logger.info(`Loading application with ID ${applicationId}`);

    return await setupSHOGunMap(parseInt(applicationId, 10));
  }

  Logger.info('No application ID given, will load the default map configuration.');

  return setupDefaultMap();
};

const setupSHOGunMap = async (applicationId: number) => {
  const client = new SHOGunAPIClient({
    url: '/api/'
  });
  const parser = new SHOGunApplicationUtil({
    client
  });

  let application;
  try {
    application = await client.application().findOne(applicationId);
  } catch (error) {
    Logger.error(`Error while loading application with ID ${applicationId}: ${error}`);
    Logger.info('Loading the default map configuration.');

    notification.error({
      message: i18n.t('Index.applicationLoadErrorMessage'),
      description: i18n.t('Index.applicationLoadErrorDescription', {
        applicationId: applicationId
      }),
      duration: 0
    });

    return setupDefaultMap();
  }

  const view = await parser.parseMapView(application);
  const layers = await parser.parseLayerTree(application);

  return new OlMap({
    view,
    layers,
    controls: OlControlDefaults({
      zoom: false
    })
  });
};

const setupDefaultMap = () => {
  const topLayer = new OlLayerTile({
    source: new OlSourceTileWMS({
      url: 'https://sgx.geodatenzentrum.de/wms_topplus_open',
      params: {
        LAYERS: 'web_light_grau'
      },
      attributions: '© <a href="http://www.bkg.bund.de" target="_blank">Bundesamt für Kartographie ' +
        `und Geodäsie (${new Date().getFullYear()})</a>, ` +
        '<a href="https://sgx.geodatenzentrum.de/web_public/gdz/datenquellen/Datenquellen_TopPlusOpen.html" ' +
        'target="_blank">Datenquellen</a>'
    }),
    properties: {
      name: 'WMS TopPlusOpen',
      hideInLayerTree: true
    }
  });

  const basemapLayer = new OlLayerTile({
    opacity: 1,
    source: new OlSourceTileWMS({
      url: 'https://sgx.geodatenzentrum.de/wms_basemapde',
      params: {
        LAYERS: 'de_basemapde_web_raster_farbe'
      },
      attributions: '© GeoBasis-DE / <a href="http://www.bkg.bund.de" target="_blank">BKG</a> ' +
        `(${new Date().getFullYear()}), <a href="https://creativecommons.org/licenses/by/4.0/">CC BY 4.0</a>`
    }),
    properties: {
      name: 'Hintergrundkarte Deutschland (basemap.de)'
    }
  });

  const wzbAttribution = '© Wissenschaftszentrum Berlin für Sozialforschung gGmbH / ' +
    '<a href="https://www.wzb.eu/de" target="_blank">WZB</a>';

  const voronoiLayer = new OlLayerTile({
    opacity: 1,
    source: new OlSourceTileWMS({
      url: '/geoserver/ows',
      params: {
        LAYERS: 'WZB:schulen_kinderarmut_voronoi'
      },
      attributions: wzbAttribution
    })
  });
  voronoiLayer.set('name', 'Kinderarmut');

  const ungleichheitenLayer = new OlLayerTile({
    opacity: 1,
    source: new OlSourceTileWMS({
      url: '/geoserver/ows',
      params: {
        LAYERS: 'WZB:bkg_ungleichheiten2022_join'
      },
      attributions: wzbAttribution
    })
  });
  ungleichheitenLayer.set('name', 'Ungleichheiten');

  const landkreiseLayer = new OlLayerTile({
    opacity: 1,
    source: new OlSourceTileWMS({
      url: '/geoserver/ows',
      params: {
        LAYERS: 'WZB:Landkreise'
      },
      attributions: wzbAttribution
    })
  });
  landkreiseLayer.set('name', 'Landkreise');

  const center = fromLonLat([10.276, 51.057], 'EPSG:3857');

  return new OlMap({
    view: new OlView({
      center: center,
      extent: transformExtent([5, 47, 15, 55], 'EPSG:4326', 'EPSG:3857'),
      zoom: 0,
      maxResolution: 2446.255411488268,
      minResolution: 0.5970703996979483,
      constrainResolution: true,
      constrainOnlyCenter: true
    }),
    layers: [
      topLayer,
      basemapLayer,
      landkreiseLayer,
      voronoiLayer,
      ungleichheitenLayer
    ],
    interactions: OlInteractionDefaults({
      altShiftDragRotate: false
    }).extend([new OlInteractionDragRotate({
      condition: evt => evt.originalEvent.ctrlKey && evt.originalEvent.shiftKey
    })]),
    controls: OlControlDefaults({
      zoom: false,
      attributionOptions: {
        collapsible: false
      }
    }).extend([
      new OlControlScaleLine({
        units: 'metric',
        text: true
      })
    ])
  });
};

const renderApp = async () => {
  try {
    const map = await setupMap();

    render(
      <React.StrictMode>
        <React.Suspense fallback={<span></span>}>
          <ConfigProvider locale={getConfigLang(i18n.language)}>
            <Provider store={store}>
              <MapContext.Provider value={map}>
                <App />
              </MapContext.Provider>
            </Provider>
          </ConfigProvider>
        </React.Suspense>
      </React.StrictMode>,
      document.getElementById('app')
    );
  } catch (error) {
    Logger.error(error);

    render(
      <React.StrictMode>
        <Alert
          className="error-boundary"
          message={i18n.t('Index.errorMessage')}
          description={i18n.t('Index.errorDescription')}
          type="error"
          showIcon
        />
      </React.StrictMode>,
      document.getElementById('app')
    );
  }
};

renderApp();
